import { LeadDto, LeadSourceDirection, LeadSourceType, LeadStatus, LEAD_STATUS_SPAM, LEADS_STATUSES } from '@sr/dto';
import { NotificationsConfig, useSnack } from 'shared/ui/snack';
import { useUpdateLeadStatus } from './lib';
import { LeadStatusPicker } from 'entities/lead/lead-status-picker';
import { commentsApi } from 'entities/comments/api';
import { useStateSelector } from 'shared/store';
import { formatPersonFull } from 'utils/person-format-helpers';

type Props = {
  lead?: LeadDto;
  showOnlyAvailableStatuses?: boolean;
};

const notificationConfig: NotificationsConfig = {
  itemTitleForm1: 'статус лида',
  itemTitleForm2: 'статуса лида',
  gender: 'male',
};

export const LeadStatusChanger = ({ lead, showOnlyAvailableStatuses = false }: Props) => {
  const { showSuccessUpdate, showFailedUpdate, showWarning, showError } = useSnack(notificationConfig);
  const { update, isUpdating } = useUpdateLeadStatus();
  const currentUser = useStateSelector((state) => state.auth.user);
  const [postLeadComment] = commentsApi.usePostLeadCommentMutation();

  const addLeadStatusComment = async (oldStatus?: string, newStatus?: string) => {
    if (!lead || !currentUser || !oldStatus || !newStatus) return;

    const actorName = formatPersonFull(currentUser).replace(/\s+/g, ' ').trim();
    if (!actorName) return;

    const content = `Пользователь ${actorName} изменил статус "${oldStatus}" на "${newStatus}"`.replace(/\s+/g, ' ').trim();
    try {
      await postLeadComment({ leadId: lead.id, dto: { content } }).unwrap();
    } catch (commentError) {
      showError('Не удалось добавить комментарий об изменении статуса', commentError);
    }
  };

  const handleStatusChange = async (value: string | number) => {
    if (!lead) return;
    const newStatus = Number(value);
    if (isNaN(newStatus) || newStatus === 0) return;

    const previousStatusId = lead.status?.id;
    try {
      await update({ leadId: lead.id, statusId: newStatus }).unwrap();

      if (previousStatusId && previousStatusId !== newStatus) {
        const oldStatusLabel = lead.status?.label ?? LEADS_STATUSES[previousStatusId as LeadStatus];
        const newStatusLabel = LEADS_STATUSES[newStatus as LeadStatus];
        await addLeadStatusComment(oldStatusLabel, newStatusLabel);
      }

      if (newStatus === LEAD_STATUS_SPAM) {
        showWarning(lead.sourceType === LeadSourceType.WAZZUP ? 'Номер добавлен в чёрный список Wazzup' : 'Емейл добавлен в чёрный список');
        return;
      }
      showSuccessUpdate();
    } catch (e) {
      showFailedUpdate(e as Error);
    }
  };

  const val = lead?.status?.id ?? '';
  const excludedStatusIds =
    lead && lead.sourceType !== LeadSourceType.WAZZUP && lead.sourceDirection !== LeadSourceDirection.IN && lead.status?.id !== LEAD_STATUS_SPAM
      ? [LEAD_STATUS_SPAM]
      : undefined;

  return (
    <LeadStatusPicker
      value={val}
      onChange={handleStatusChange}
      label="Статус"
      disabled={!lead || isUpdating}
      showOnlyAvailableStatuses={showOnlyAvailableStatuses}
      excludedStatusIds={excludedStatusIds}
    />
  );
};
